import { type IconType } from '@assembly-js/design-system';
import { omit } from 'lodash';
import { v4 } from 'uuid';

import {
  AppAPIName,
  EmbedlyData,
  ExtensionConfig,
  ExtensionsSettings,
  ModuleSettingsItem,
  ModuleType,
  UnAuthAPIName,
  VisibilityRule,
} from 'src/constants';
import {
  AppNotificationConfig,
  MarketplaceApplication,
} from 'src/constants/dataTypes/appsTypes';
import { DEFAULT_APPS } from 'src/hooks/useApps';
import { insertModuleSettingsItem } from 'src/legacy/components/Settings/AppSetupPage/folderUtils';
import { shouldNewExtensionBeRoot } from 'src/legacy/components/Settings/AppSetupPage/SetupApp/resolveRootModuleId';
import { RootState } from 'src/store/reduxTypes';
import {
  updateExtensionSettingsAction,
  updateModuleSettings,
} from 'src/store/settings/actions';
import { API } from 'src/utils/AmplifyApiUtils';
import { IsLoggedIn } from 'src/utils/AuthUtils';
import { ensureApiError } from 'src/utils/Errors';

import { settleQueryFulfilled } from './settleQueryFulfilled';

import { ApiMethods, ApiTags, appAPI } from '.';

type AppInstallType =
  | 'core'
  | 'manual'
  | 'link'
  | 'embed'
  | 'custom'
  | 'marketplace'
  | 'folder';
export interface AppInstall {
  id: string;
  displayName: string;
  type: AppInstallType;
  appId: string;
  content: string;
  icon: string;
  sort: number;
  disabled: boolean;
  embedCount: number;
  isAuthenticated: boolean;
  autoSizeEmbed: boolean;
  embedlyData: EmbedlyData;
  embedCode: string;
  embedLink: string;
  visibilityConfig?: VisibilityRule[];
  queryParams?: { key: string; value: string }[];
  companyId?: string;
  path?: string;
  iuSidebarHidden: boolean;
  isInternalApp?: boolean;
  supportsExpiringTokens?: boolean;
  isDraft?: boolean;
  appBuilderId?: string;
  isRoot?: boolean;
  notificationConfig?: AppNotificationConfig;
}

export interface MarketplaceApplicationWebhook {
  eventName: string;
  eventDescription: string;
  // Display name of the app that registered the event, so the settings picker can
  // disambiguate two apps' identically-named events. Absent for native events.
  appName?: string;
}

/**
 * The visible module ids and custom-app ids for a client within one company.
 * Stored as plain arrays so the query result stays serializable. Returned per
 * company because the same client can have different app visibility in each.
 */
export interface ClientCompanyVisibility {
  companyId: string;
  moduleIds: string[];
  appIds: string[];
}

const appTypeMap: Record<AppInstallType, ModuleType> = {
  core: 'module',
  manual: 'local',
  link: 'global',
  embed: 'global',
  custom: 'global',
  marketplace: 'global',
  folder: 'folder',
};

const mapInstallsToModuleSettings = (
  installs: AppInstall[],
): ModuleSettingsItem[] => {
  const apps = installs.map((install) => {
    return {
      id: install.id,
      label: install.displayName,
      icon: install.icon,
      type: appTypeMap[install.type],
      disabled: install.disabled,
      visibilityConfig: install.visibilityConfig,
      path: install.path,
      iuSidebarHidden: install.iuSidebarHidden,
      isInternalApp: install.isInternalApp,
    };
  });
  // installs api doesn't return disabled modules so we should add the
  // default core modules as disabled here
  DEFAULT_APPS.forEach((module) => {
    if (!apps.find((i) => i.id === module.id)) {
      apps.push({
        id: module.id,
        label: module.title,
        icon: module.iconName,
        type: 'module',
        disabled: true,
        visibilityConfig: undefined,
        path: undefined,
        iuSidebarHidden: false,
        isInternalApp: false,
      });
    }
  });
  return apps;
};

/**
 * Query type discriminator for getInstalls endpoint.
 * Using a single string discriminator ensures consistent cache keys.
 * - undefined/void: default query (always includes folders)
 * - 'preview': previews as client (previewAsClient=true)
 */
export type GetInstallsQueryType = 'preview';

const mapInstallsToExtensionsSettings = (
  installs: AppInstall[],
): ExtensionsSettings => {
  const settings: Record<string, ExtensionConfig> = {};
  installs.forEach((install) => {
    const appType = appTypeMap[install.type];
    if (appType == 'global' || appType == 'local') {
      settings[install.id] = {
        type: appType,
        sort: install.sort ?? 0,
        icon: install.icon as IconType,
        name: install.displayName,
        embedCount: install.embedCount,
        isAuthenticated: install.isAuthenticated,
        appId: install.appId,
        embedCode: install.embedCode || install.content,
        embedLink: install.embedLink,
        autoSizeEmbed: install.autoSizeEmbed,
        embedlyData: install.embedlyData,
        queryParams: install.queryParams,
        companyId: install.companyId,
        supportsExpiringTokens: install.supportsExpiringTokens,
        isDraft: install.isDraft,
        appBuilderId: install.appBuilderId,
        isRoot: install.isRoot,
        notificationConfig: install.notificationConfig,
      };
    }
  });
  return settings;
};

export const applicationSettingsApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createApp: build.mutation<
      MarketplaceApplication,
      Omit<MarketplaceApplication, 'id'> & {
        icon: IconType;
        folderId?: string;
        queryParams?: Array<{ key: string; value: string }>;
        /**
         * Client-side extension config id for the new install. Passed in so the
         * caller can deep-link to the created app's page; falls back to a fresh
         * UUID when omitted. Not part of the backend `/apps` payload.
         */
        moduleConfigId?: string;
      }
    >({
      query: (body) => {
        const requestBody = omit(body, 'moduleConfigId');
        return {
          method: ApiMethods.post,
          options: { body: requestBody },
          path: '/apps',
          body: requestBody,
        };
      },
      async onQueryStarted(input, { dispatch, getState, queryFulfilled }) {
        // This is terrible. RTK offers no solution for typing getState
        // so we have to resort to typecasting.
        const state = getState() as unknown as RootState;
        const installsData =
          applicationSettingsApi.endpoints.getInstalls.select(undefined)(state);
        const { moduleSettings, extensionsSettings } = installsData.data ?? {};

        const settled = await settleQueryFulfilled(queryFulfilled, 'createApp');
        if (!settled.ok) return;
        const {
          data: { id: appId },
        } = settled.result;

        const newExtensionModuleConfigId = input.moduleConfigId ?? v4();

        // TODO(foley): what do we do about sort? do we even need it on the extension?
        const newExtensionModule: ExtensionConfig = {
          id: newExtensionModuleConfigId,
          type: 'global' as const,
          isAuthenticated: true,
          icon: input.icon,
          name: input.name,
          appId,
          isRoot: shouldNewExtensionBeRoot(extensionsSettings, appId),
          autoSizeEmbed: true,
          sort: 0, // sort is not used any more. All extensionConfig objects has sort 0
          queryParams: input.queryParams ?? [],
        };

        const moduleSettingsItem: ModuleSettingsItem = {
          id: newExtensionModuleConfigId,
          label: input.name,
          icon: input.icon,
          disabled: false,
          type: 'global',
          visibilityConfig: input.visibilityConfig,
          iuSidebarHidden: false,
          isInternalApp: !input.clientUrl,
        };

        // when app is created, optimistically perform install side-effects
        // which includes updating the moduleSettings and extensionsSettings
        const patch = dispatch(
          applicationSettingsApi.util.updateQueryData(
            'getInstalls',
            undefined,
            (draftState) => {
              draftState.moduleSettings = insertModuleSettingsItem(
                draftState.moduleSettings,
                moduleSettingsItem,
                input.folderId,
              );
              draftState.extensionsSettings[newExtensionModuleConfigId] =
                newExtensionModule;
            },
          ),
        );

        try {
          // The order is important here. We need to update the extension settings
          // before the module settings, because the module settings depend on the
          // extension settings.
          await dispatch(
            updateExtensionSettingsAction({
              ...(extensionsSettings ?? {}),
              [newExtensionModuleConfigId]:
                newExtensionModule as ExtensionConfig,
            }),
          );

          await dispatch(
            updateModuleSettings(
              insertModuleSettingsItem(
                moduleSettings ?? [],
                moduleSettingsItem,
                input.folderId,
              ),
              {
                disableSuccessAlert: true,
              },
            ),
          );
        } catch (e) {
          await patch.undo();
        }
      },
      // No `apps` invalidation: this mutation already maintains the getInstalls
      // cache optimistically and patches it with server truth via the module /
      // extension settings actions above. Refetching here would race those
      // writes — a stale /v1/installs response can briefly drop the new install,
      // bouncing a user who just deep-linked to the created app back to home.
    }),
    updateApp: build.mutation<
      MarketplaceApplication,
      Pick<MarketplaceApplication, 'id' | 'name' | 'portalId'> &
        Partial<
          Pick<
            MarketplaceApplication,
            | 'clientUrl'
            | 'internalUrl'
            | 'notificationUrl'
            | 'webhookUrl'
            | 'webhookEvents'
            | 'notificationConfig'
            | 'visibilityConfig'
          >
        > & {
          icon: IconType;
          moduleId: string;
          queryParams?: Array<{ key: string; value: string }>;
        }
    >({
      query: (body) => ({
        method: ApiMethods.put,
        options: {
          body: omit(body, 'id', 'icon', 'moduleId', 'queryParams'),
        },
        path: `/apps/${body.id}`,
      }),
      async onQueryStarted(input, { dispatch, getState, queryFulfilled }) {
        // This is terrible. RTK offers no solution for typing getState
        // so we have to resort to typecasting.
        const state = getState() as unknown as RootState;
        const installsData =
          applicationSettingsApi.endpoints.getInstalls.select(undefined)(state);
        const { moduleSettings, extensionsSettings } = installsData.data ?? {};
        const moduleSettingsItem = (moduleSettings ?? []).find(
          (m) => m.id === input.moduleId,
        );
        if (!moduleSettings || !moduleSettingsItem || !input.moduleId) {
          // TODO(foley): Implement error handling.
          return;
        }

        const extensionSettingsItem = extensionsSettings?.[input.moduleId];
        if (!extensionSettingsItem) {
          // TODO(foley): Implement error handling.
          return;
        }

        const settled = await settleQueryFulfilled(queryFulfilled, 'updateApp');
        if (!settled.ok) return;

        // The order is important here. We need to update the extension settings
        // before the module settings, because the module settings depend on the
        // extension settings.

        await dispatch(
          updateExtensionSettingsAction({
            ...(extensionsSettings ?? {}),
            [input.moduleId]: {
              ...extensionSettingsItem,
              name: input.name,
              // If icon ever gets set to '' it causes trouble, so let's
              // make sure that does not happen.
              icon: input.icon || ('dashboard' as IconType),
              queryParams:
                input.queryParams ?? extensionSettingsItem.queryParams,
            },
          }),
        );

        await dispatch(
          updateModuleSettings(
            moduleSettings.map((m) => {
              if (m.id === input.moduleId) {
                return {
                  ...m,
                  label: input.name,
                  icon: input.icon || 'dashboard',
                  isInternalApp: !settled.result.data.clientUrl,
                };
              }
              return m;
            }),
            {
              disableSuccessAlert: true,
            },
          ),
        );
      },
      invalidatesTags: [ApiTags.apps],
    }),
    getApp: build.query<MarketplaceApplication, { id: string }>({
      query: ({ id }) => ({
        path: `/apps/${id}`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: {},
        },
      }),
      providesTags: [ApiTags.apps],
    }),
    deleteApp: build.mutation<MarketplaceApplication, { id: string }>({
      query: ({ id }) => ({
        path: `/apps/${id}`,
        method: ApiMethods.del,
        options: {
          queryStringParameters: {},
        },
      }),
    }),
    getInstalls: build.query<
      {
        moduleSettings: ModuleSettingsItem[];
        extensionsSettings: ExtensionsSettings;
      },
      GetInstallsQueryType | void
    >({
      queryFn: async (queryType) => {
        const previewAsClient = queryType === 'preview';

        const queryParams = new URLSearchParams();
        if (previewAsClient) queryParams.set('previewAsClient', 'true');
        const queryString = queryParams.toString();

        const isLoggedInUser = await IsLoggedIn();
        const installs = await API.get(
          isLoggedInUser ? AppAPIName : UnAuthAPIName,
          `${isLoggedInUser ? '' : '/public'}/v1/installs${queryString ? `?${queryString}` : ''}`,
          {},
        );
        // map installs into their respective moduleSettings and extensionsSettings
        return {
          data: {
            moduleSettings: mapInstallsToModuleSettings(installs),
            extensionsSettings: mapInstallsToExtensionsSettings(installs),
          },
        };
      },
      providesTags: [ApiTags.apps],
    }),
    // App visibility is per-company, so fetch the client's installs once per
    // company (scoping each request with the Company header) and return the
    // visible module/app ids grouped by company. Lets a multi-company client's
    // notification badge gate each company independently of the active one.
    getClientCompanyVisibility: build.query<
      ClientCompanyVisibility[],
      string[]
    >({
      queryFn: async (companyIds) => {
        const settled = await Promise.allSettled(
          companyIds.map(async (companyId) => {
            const installs: AppInstall[] = await API.get(
              AppAPIName,
              '/v1/installs',
              { headers: { Company: companyId } },
            );
            const moduleIds: string[] = [];
            const appIds: string[] = [];
            for (const install of installs) {
              if (install.disabled) continue;
              moduleIds.push(install.id);
              if (install.appId) appIds.push(install.appId);
            }
            return { companyId, moduleIds, appIds };
          }),
        );
        const data = settled
          .filter(
            (
              result,
            ): result is PromiseFulfilledResult<ClientCompanyVisibility> =>
              result.status === 'fulfilled',
          )
          .map((result) => result.value);

        // A rejected company is otherwise invisible: it would just be absent
        // from `data`, so its badge silently reads 0 with no error state.
        // Surface the failures so they're observable, and only fail the whole
        // query when every company failed — a single transient 5xx shouldn't
        // blank every company's badge, but a total failure should error (and
        // retry) rather than cache an empty success.
        const rejected = settled.filter(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected',
        );
        if (rejected.length > 0) {
          console.error(
            `getClientCompanyVisibility: ${rejected.length}/${companyIds.length} company installs requests failed`,
            rejected.map((result): unknown => result.reason),
          );
        }
        if (companyIds.length > 0 && data.length === 0) {
          const error = ensureApiError(rejected.at(0)?.reason);
          return { error };
        }
        return { data };
      },
      // Deliberately not tagged with ApiTags.apps: per-company visibility is the
      // same regardless of which company is active, so it must NOT be refetched
      // on every company switch (that would re-fire one /v1/installs call per
      // company). It only changes when an admin edits visibility, which arrives
      // via PortalSettingsHandler and invalidates this dedicated tag.
      providesTags: [ApiTags.clientCompanyVisibility],
    }),
    getAppInstallEmbedCode: build.query<
      string,
      {
        appId: string;
        userId?: string;
        companyId?: string;
        notificationId?: string;
      }
    >({
      query: ({ appId, userId, companyId, notificationId }) => ({
        method: ApiMethods.get,
        path: `/v1/app-installs/${appId}/embed`,
        options: {
          queryStringParameters: {
            userId,
            companyId,
            notificationId,
          },
        },
      }),
    }),
    getWebhookEvents: build.query<MarketplaceApplicationWebhook[], void>({
      query: () => ({
        path: '/apps/webhook-events',
        method: ApiMethods.get,
        options: {
          queryStringParameters: {},
        },
      }),
    }),
    createDevToken: build.mutation<
      { token: string; supportsExpiringTokens: boolean },
      {
        appId: string;
        previewAsClientUserId?: string;
        previewAsCompanyId?: string;
      }
    >({
      query: ({ appId, previewAsClientUserId, previewAsCompanyId }) => {
        const queryStringParameters: Record<string, string> = {};
        if (previewAsClientUserId)
          queryStringParameters.previewAsClientUserId = previewAsClientUserId;
        if (previewAsCompanyId)
          queryStringParameters.previewAsCompanyId = previewAsCompanyId;
        return {
          path: `/v1/app-installs/${appId}/dev-token`,
          method: ApiMethods.post,
          options: Object.keys(queryStringParameters).length
            ? { queryStringParameters }
            : {},
        };
      },
    }),
    suggestIcon: build.mutation<{ icon: string }, { appName: string }>({
      query: ({ appName }) => ({
        path: '/v1/ai/icon',
        method: ApiMethods.get,
        options: { queryStringParameters: { appName } },
      }),
    }),
  }),
});

export const {
  useCreateAppMutation,
  useGetAppQuery,
  useUpdateAppMutation,
  useDeleteAppMutation,
  useGetInstallsQuery,
  useGetClientCompanyVisibilityQuery,
  useGetAppInstallEmbedCodeQuery,
  useGetWebhookEventsQuery,
  useCreateDevTokenMutation,
  useSuggestIconMutation,
} = applicationSettingsApi;
